import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const BMICalculator = () => {
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState('');
  const [result, setResult] = useState(null);

  const getBMICategory = (bmi) => {
    if (bmi < 18.5) return { category: 'Untergewicht', color: 'text-blue-400' };
    if (bmi < 25) return { category: 'Normalgewicht', color: 'text-green-400' };
    if (bmi < 30) return { category: 'Übergewicht', color: 'text-yellow-400' };
    return { category: 'Adipositas', color: 'text-red-400' };
  };

  const calculateBMI = () => {
    const w = parseFloat(weight);
    const h = parseFloat(height);

    if (!w || !h || w <= 0 || h <= 0) {
      setResult(null);
      return;
    }

    const heightInMeters = h / 100;
    const bmi = w / (heightInMeters * heightInMeters);
    const category = getBMICategory(bmi);

    setResult({
      bmi: bmi.toFixed(1),
      ...category
    });
  };

  useEffect(() => {
    calculateBMI();
  }, [weight, height]);

  return (
    <div className="space-y-6">
      {/* Input Fields */}
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-white font-medium mb-2">
            Gewicht (kg)
          </label>
          <input
            type="number"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            placeholder="z.B. 70"
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-400"
          />
        </div>
        <div>
          <label className="block text-white font-medium mb-2">
            Größe (cm)
          </label>
          <input
            type="number"
            value={height}
            onChange={(e) => setHeight(e.target.value)}
            placeholder="z.B. 175"
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-400"
          />
        </div>
      </div>

      {/* Results */}
      {result && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/5 rounded-xl p-6 border border-white/10 text-center"
        >
          <h3 className="text-2xl font-bold text-white mb-4">
            Ihr BMI
          </h3>
          
          <div className="text-5xl font-bold text-purple-300 mb-2">
            {result.bmi}
          </div>
          
          <div className={`text-xl font-semibold ${result.color} mb-4`}>
            {result.category}
          </div>

          <div className="text-sm text-white/70 max-w-md mx-auto">
            <p>Der Body-Mass-Index ist ein Richtwert zur Bewertung des Körpergewichts in Relation zur Körpergröße.</p>
          </div>

          {/* BMI Scale */}
          <div className="mt-6 space-y-2 text-sm">
            <div className="flex justify-between items-center">
              <span className="text-blue-400">Untergewicht</span>
              <span className="text-white/70">&lt; 18.5</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-green-400">Normalgewicht</span>
              <span className="text-white/70">18.5 - 24.9</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-yellow-400">Übergewicht</span>
              <span className="text-white/70">25.0 - 29.9</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-red-400">Adipositas</span>
              <span className="text-white/70">&geq; 30.0</span>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default BMICalculator;